import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  PlayCircle, Lock, CheckCircle2, Loader2, ShoppingBag, User, ArrowLeft,
  Sparkles, BookOpen, ListChecks, HelpCircle, ClipboardList, Award, Download, MessageSquareText,
  Library, Swords, Circle,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AiNotesPanel } from "@/components/video-notes/AiNotesPanel";
import { ManualNotesPanel } from "@/components/video-notes/ManualNotesPanel";
import { QuizPanel } from "@/components/video-notes/QuizPanel";
import { AssignmentsPanel } from "@/components/video-notes/AssignmentsPanel";
import { ResourcesPanel } from "@/components/video-notes/ResourcesPanel";
import { BossBattlePanel } from "@/components/video-notes/BossBattlePanel";
import { CourseReviewsPanel } from "@/components/CourseReviewsPanel";
import { StarRating } from "@/components/StarRating";
import { CareerToolsCTA } from "@/components/CareerToolsCTA";

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function CourseDetailPage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [tab, setTab] = useState("ai_notes");
  const [buying, setBuying] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [confirmingPurchase, setConfirmingPurchase] = useState(!!params.get("session_id"));
  const [markingComplete, setMarkingComplete] = useState(false);

  const load = useCallback(() => {
    return api.get(`/courses/${courseId}`).then((res) => {
      setCourse(res.data);
      if (!activeVideoId && res.data.videos.length > 0) setActiveVideoId(res.data.videos[0].id);
      return res.data;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => {
    setLoading(true);
    load().catch(() => toast.error("Course not found")).finally(() => setLoading(false));
  }, [load]);

  // Poll a pending Stripe checkout after redirect back from success_url
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId || params.get("purchase") !== "success") { setConfirmingPurchase(false); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Purchase confirmed! Course unlocked.");
          setConfirmingPurchase(false);
          load();
          return;
        }
        if (data.status === "expired") { setConfirmingPurchase(false); return; }
      } catch {
        setConfirmingPurchase(false);
        return;
      }
      attempts += 1;
      if (attempts >= MAX_POLL_ATTEMPTS) { setConfirmingPurchase(false); return; }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll processing videos (AI notes generating in background)
  useEffect(() => {
    if (!course) return;
    const anyProcessing = course.videos.some((v) => v.transcript_status === "processing");
    if (!anyProcessing) return;
    const t = setTimeout(() => load(), 5000);
    return () => clearTimeout(t);
  }, [course, load]);

  const handleBuy = async () => {
    if (!user) { toast.error("Please log in to purchase this course"); return; }
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "course_purchase", course_id: courseId, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setBuying(false);
    }
  };

  const handleVideoSelect = (id) => {
    setVideoError(false);
    setActiveVideoId(id);
  };

  const toggleComplete = async (videoId) => {
    setMarkingComplete(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/videos/${videoId}/complete`);
      setCourse((prev) => ({ ...prev, videos: prev.videos.map((v) => (v.id === videoId ? { ...v, completed: data.completed } : v)) }));
      toast.success(data.completed ? "Marked as complete!" : "Marked as incomplete");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setMarkingComplete(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="course-detail-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!course) return null;

  const activeVideo = course.videos.find((v) => v.id === activeVideoId) || course.videos[0];
  const videoLocked = activeVideo?.locked;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="course-detail-page">
      <Link to="/courses" data-testid="course-detail-back-link" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Courses
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white" data-testid="course-detail-title">{course.title}</h1>
          {course.review_count > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5" data-testid="course-detail-rating">
              <StarRating rating={course.avg_rating} size="w-4 h-4" />
              <span className="text-sm font-semibold text-white">{course.avg_rating}</span>
              <span className="text-xs text-slate-500">({course.review_count} review{course.review_count === 1 ? "" : "s"})</span>
            </div>
          )}
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">{course.description}</p>
          {course.creator_handle && (
            <Link to={`/creator/${course.creator_handle}`} data-testid="course-detail-creator-link" className="inline-flex items-center gap-1.5 text-xs text-pink-300 hover:text-pink-200 mt-2">
              <User className="w-3.5 h-3.5" /> {course.creator_name}
            </Link>
          )}
        </div>
        {!course.has_access && (
          <div className="glass-panel rounded-xl p-4 min-w-[220px]" data-testid="course-detail-purchase-card">
            <div className="text-2xl font-bold text-amber-300 mb-3">{course.price > 0 ? `₹${course.price.toLocaleString("en-IN")}` : "Free"}</div>
            <button onClick={handleBuy} disabled={buying} data-testid="course-detail-buy-btn"
              className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white font-semibold text-sm py-2.5 rounded-lg transition-colors">
              {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
              {buying ? "Redirecting..." : "Buy Course"}
            </button>
          </div>
        )}
        {course.has_access && !course.is_owner && (
          <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-3 py-1.5 rounded-lg" data-testid="course-detail-owned-badge">
            <CheckCircle2 className="w-4 h-4" /> You have access to this course
          </span>
        )}
      </div>

      {confirmingPurchase && (
        <div className="glass-panel rounded-xl p-4 flex items-center gap-2 text-sm text-slate-300 mb-6" data-testid="course-detail-confirming-purchase">
          <Loader2 className="w-4 h-4 animate-spin text-amber-400" /> Confirming your payment...
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Player + Notes */}
        <div className="lg:col-span-2 space-y-4">
          <div className="w-full aspect-video rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden relative" data-testid="course-detail-video-player">
            {!activeVideo ? (
              <p className="text-sm text-slate-500">No videos yet.</p>
            ) : videoLocked ? (
              <div className="text-center px-6" data-testid="course-detail-video-locked">
                <Lock className="w-9 h-9 text-slate-500 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Purchase this course to unlock this lecture.</p>
              </div>
            ) : (
              <video
                key={activeVideo.id}
                src={`${api.defaults.baseURL}/courses/${courseId}/videos/${activeVideo.id}/stream`}
                controls
                className="w-full h-full"
                data-testid="course-detail-video-element"
                onError={() => setVideoError(true)}
              />
            )}
            {!videoLocked && activeVideo && videoError && (
              <div className="absolute inset-0 bg-background/95 flex flex-col items-center justify-center px-6 text-center" data-testid="course-detail-video-error">
                <Lock className="w-9 h-9 text-slate-500 mb-2" />
                <p className="text-sm text-slate-400">Couldn't load this video. If you just purchased, refresh the page.</p>
              </div>
            )}
          </div>

          {activeVideo && !videoLocked && (
            <button onClick={() => toggleComplete(activeVideo.id)} disabled={markingComplete} data-testid="course-detail-mark-complete-btn"
              className={`w-full flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-60 ${activeVideo.completed ? "bg-emerald-400/10 border border-emerald-400/30 text-emerald-300" : "bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300"}`}>
              {markingComplete ? <Loader2 className="w-4 h-4 animate-spin" /> : activeVideo.completed ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
              {activeVideo.completed ? "Completed" : "Mark as Complete"}
            </button>
          )}

          {activeVideo && !videoLocked && (
            <div className="glass-panel rounded-2xl overflow-hidden">
              <div className="flex border-b border-white/10 overflow-x-auto">
                <button onClick={() => setTab("ai_notes")} data-testid="course-detail-tab-ai-notes"
                  className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-3 transition-colors whitespace-nowrap px-3 ${tab === "ai_notes" ? "text-amber-300 border-b-2 border-amber-300" : "text-slate-400 hover:text-white"}`}>
                  <Sparkles className="w-4 h-4" /> AI Notes
                </button>
                <button onClick={() => setTab("manual_notes")} data-testid="course-detail-tab-manual-notes"
                  className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-3 transition-colors whitespace-nowrap px-3 ${tab === "manual_notes" ? "text-amber-300 border-b-2 border-amber-300" : "text-slate-400 hover:text-white"}`}>
                  <BookOpen className="w-4 h-4" /> Course Notes
                </button>
                <button onClick={() => setTab("quiz")} data-testid="course-detail-tab-quiz"
                  className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-3 transition-colors whitespace-nowrap px-3 ${tab === "quiz" ? "text-amber-300 border-b-2 border-amber-300" : "text-slate-400 hover:text-white"}`}>
                  <HelpCircle className="w-4 h-4" /> Quiz
                </button>
                <button onClick={() => setTab("assignments")} data-testid="course-detail-tab-assignments"
                  className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-3 transition-colors whitespace-nowrap px-3 ${tab === "assignments" ? "text-amber-300 border-b-2 border-amber-300" : "text-slate-400 hover:text-white"}`}>
                  <ClipboardList className="w-4 h-4" /> Assignments
                </button>
                <button onClick={() => setTab("resources")} data-testid="course-detail-tab-resources"
                  className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-3 transition-colors whitespace-nowrap px-3 ${tab === "resources" ? "text-amber-300 border-b-2 border-amber-300" : "text-slate-400 hover:text-white"}`}>
                  <Library className="w-4 h-4" /> Resources
                </button>
                <button onClick={() => setTab("boss_battle")} data-testid="course-detail-tab-boss-battle"
                  className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-3 transition-colors whitespace-nowrap px-3 ${tab === "boss_battle" ? "text-amber-300 border-b-2 border-amber-300" : "text-slate-400 hover:text-white"}`}>
                  <Swords className="w-4 h-4" /> Boss Battle
                </button>
              </div>
              <div className="p-5">
                {tab === "ai_notes" && <AiNotesPanel video={activeVideo} />}
                {tab === "manual_notes" && <ManualNotesPanel notes={course.manual_notes} />}
                {tab === "quiz" && <QuizPanel courseId={courseId} videoId={activeVideo.id} />}
                {tab === "assignments" && <AssignmentsPanel courseId={courseId} />}
                {tab === "resources" && <ResourcesPanel resources={course.resources} />}
                {tab === "boss_battle" && <BossBattlePanel courseId={courseId} battles={course.boss_battles} onSubmitted={load} />}
              </div>
            </div>
          )}

          {course.certificate_id && (
            <a href={`${api.defaults.baseURL}/creator/certificates/${course.certificate_id}/pdf`} data-testid="course-detail-download-certificate-btn"
              className="flex items-center justify-center gap-2 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 text-amber-300 font-semibold text-sm py-3 rounded-xl transition-colors">
              <Award className="w-4 h-4" /> Download Your Certificate <Download className="w-3.5 h-3.5" />
            </a>
          )}

          <div className="glass-panel rounded-2xl p-5" data-testid="course-detail-reviews-section">
            <h3 className="font-heading text-sm font-semibold text-white mb-4 flex items-center gap-1.5"><MessageSquareText className="w-4 h-4 text-pink-400" /> Reviews</h3>
            <CourseReviewsPanel courseId={courseId} canReview={course.can_review} myReview={course.my_review} />
          </div>
        </div>

        {/* Curriculum sidebar */}
        <div className="glass-panel rounded-2xl p-4 self-start" data-testid="course-detail-curriculum">
          <h3 className="font-heading text-sm font-semibold text-white mb-3 flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-pink-400" /> Curriculum ({course.videos.length})</h3>
          <div className="space-y-1.5">
            {course.videos.map((v, i) => (
              <button
                key={v.id} onClick={() => !v.locked && handleVideoSelect(v.id)} data-testid={`course-detail-video-row-${v.id}`}
                className={`w-full flex items-center gap-2.5 text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${activeVideoId === v.id ? "bg-primary/20 text-white" : "text-slate-400 hover:bg-white/5"} ${v.locked ? "cursor-not-allowed opacity-60" : ""}`}
              >
                {v.locked ? <Lock className="w-3.5 h-3.5 shrink-0" /> : v.transcript_status === "processing" ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-amber-400" /> : <PlayCircle className="w-3.5 h-3.5 shrink-0" />}
                <span className="truncate">{i + 1}. {v.title}</span>
              </button>
            ))}
            {course.videos.length === 0 && <p className="text-xs text-slate-500 px-1">No lectures uploaded yet.</p>}
          </div>
        </div>
      </div>
      <CareerToolsCTA />
    </div>
  );
}
